import { Button, Flex, Switch, Skeleton } from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { v4 as uuidV4 } from 'uuid';

import { getLoanActivity } from '../activity';
import { LoanActivity, EditLoanFormFields } from '../types';
import {
  EditLoanContextReady,
  useEditLoanContext,
} from '../useEditLoanContext';
import { Typography } from '~/packages/common-frontend/components/Typography';

const SideBarItem = ({ label, path }: { label: string; path: string }) => {
  const { pathname } = useLocation();
  const isPath = matchPath(pathname, path);

  return (
    <Button
      as={Link}
      to={path}
      variant={isPath ? 'primary-muted' : 'ghost'}
      colorScheme="v2-aurora"
      w="100%"
      justifyContent="flex-start"
    >
      {label}
    </Button>
  );
};

const NavigationSideBarInner = ({
  context,
}: {
  context: EditLoanContextReady;
}) => {
  const { watch, getFieldState } = useFormContext<EditLoanFormFields>();
  const currentFundingRequests = watch('fundingRequests');

  const [showOnlyEdited, setShowOnlyEdited] = useState(false);

  const filteredActivity = useMemo<LoanActivity[]>(() => {
    const unfilteredActivity = currentFundingRequests
      ? getLoanActivity({ context, currentFundingRequests })
      : [];

    if (showOnlyEdited) {
      return unfilteredActivity.filter((activityItem) => activityItem.isEdited);
    }

    return unfilteredActivity;
  }, [context, currentFundingRequests, showOnlyEdited]);

  const showLoanMetadata = useMemo<boolean>(() => {
    if (!showOnlyEdited) {
      return true;
    }

    // From here, only care about determining if anything changed
    const { newEntities, originalResolvedLoan } = context.state;
    if (!newEntities) {
      return (
        getFieldState('externalLoanId').isDirty ||
        getFieldState('pricingOptionKey').isDirty ||
        getFieldState('maturityDate').isDirty
      );
    }

    const { newResolvedLoan } = newEntities;
    return (
      originalResolvedLoan.externalLoanId !== newResolvedLoan.externalLoanId ||
      originalResolvedLoan.pricing.key !== newResolvedLoan.pricing.key ||
      originalResolvedLoan.dates.maturityDate !==
        newResolvedLoan.dates.maturityDate
    );
  }, [context.state, showOnlyEdited, getFieldState]);

  return (
    <Flex
      w="30rem"
      h="100vh"
      padding="1rem"
      gap="3rem"
      flexDirection="column"
      alignItems="flex-start"
      borderColor="gray.100"
      borderRadius="0.5rem"
      borderWidth="1px"
    >
      <br />
      <Flex gap="1rem">
        <Switch
          isChecked={showOnlyEdited}
          onChange={() => setShowOnlyEdited((prev) => !prev)}
        />
        <Typography>Show Edited Only</Typography>
      </Flex>

      <Flex
        flexDirection="column"
        alignContent="flex-start"
        gap="1rem"
        w="100%"
      >
        {showLoanMetadata && (
          <SideBarItem
            label="Loan Details"
            path={`/loans/${context.state.originalResolvedLoan.id}/edit/review`}
          />
        )}
        {filteredActivity.map((activityItem) =>
          activityItem.type === 'Payment' ? (
            <SideBarItem
              key={activityItem.activityId || uuidV4()}
              label={activityItem.type} // TODO create invoice navigation
              path={`/loans/${context.state.originalResolvedLoan.id}/edit/review/invoice/:invoiceId`}
            />
          ) : (
            <SideBarItem
              key={activityItem.activityId || uuidV4()}
              label={activityItem.type} // TODO pipe in funding request type to label as origination/ upsize
              path={`/loans/${context.state.originalResolvedLoan.id}/edit/review/fundingRequests/${activityItem.activityId}`}
            />
          ),
        )}
      </Flex>
    </Flex>
  );
};

export const NavigationSideBar = () => {
  const context = useEditLoanContext();
  const { pathname } = useLocation();

  if (context.status !== 'ready') {
    return (
      <Flex
        w="30rem"
        h="100vh"
        padding="1rem"
        gap="3rem"
        flexDirection="column"
        alignItems="flex-start"
        borderColor="gray.100"
        borderRadius="0.5rem"
        borderWidth="1px"
      >
        <br />
        <Flex gap="1rem">
          <Switch isDisabled />
          <Typography>Show Edited Only</Typography>
        </Flex>
        <Flex
          flexDirection="column"
          alignContent="flex-start"
          gap="1rem"
          w="100%"
        >
          <SideBarItem label="Loan Details" path={pathname} />
          <Skeleton w="100%" h="2.5rem" borderRadius="0.25rem" />
          <Skeleton w="100%" h="2.5rem" borderRadius="0.25rem" />
          <Skeleton w="100%" h="2.5rem" borderRadius="0.25rem" />
        </Flex>
      </Flex>
    );
  }

  return <NavigationSideBarInner context={context} />;
};
